export interface AuthTokenProvider {
  /** Resolve the token to attach to agent-bot requests. */
  get(): Promise<string>;
  /** Drop the cached token (e.g. after a 401) so the next get re-mints. */
  invalidate(): void;
}

interface MintedToken {
  token: string;
  /** Epoch milliseconds. */
  expiry: number;
}

/**
 * Re-mint this long before the server-side expiry so a token never
 * goes stale mid-flight.
 */
const EXPIRY_MARGIN_MS = 30 * 1000;

/**
 * Deferred import: the feathers client bootstraps against `window` at
 * module init, so a static import here would break any module graph
 * that includes the chat transport in a non-browser environment.
 */
async function mintViaFeathers(): Promise<MintedToken> {
  const { loadApiAuthToken } = await import('@sbrc/services/feathers-client');

  return loadApiAuthToken();
}

/**
 * Caches the short-lived robot-API token minted over the authenticated
 * Feathers session (`apiAuth` service → bot DB), re-minting lazily as
 * expiry approaches. Concurrent callers share one in-flight mint.
 */
export function createApiAuthTokenProvider(
  fetchToken: () => Promise<MintedToken> = mintViaFeathers,
): AuthTokenProvider {
  let cached: MintedToken | null = null;
  let inflight: Promise<MintedToken> | null = null;

  return {
    async get() {
      if (cached && cached.expiry - EXPIRY_MARGIN_MS > Date.now()) {
        return cached.token;
      }

      if (!inflight) {
        inflight = fetchToken().finally(() => {
          inflight = null;
        });
      }

      cached = await inflight;

      return cached.token;
    },

    invalidate() {
      cached = null;
    },
  };
}
